/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import {
  STATUS_CONFIG,
  type ReservationRequest,
  type ReservationRequestStatus,
  type LinkedReservation,
  type TableAssignment,
} from "./types";
import { formatTime } from "./utils";
import { useAuth } from "@app/vue/providers/Auth";
import { ExternalLink } from "@app/vue/components/ExternalLink";

export interface ReservationRequestDetailsProps {
  request: ReservationRequest | null;
  linkedReservation?: LinkedReservation | null;
  isOpen: boolean;
  onClose: () => void;
  /** Parent opens AcceptReservationRequest sheet */
  onAcceptIntent?: () => void;
  /** Parent opens RejectReservationRequest sheet */
  onRejectIntent?: () => void;
  /** Parent calls reject mutation with status "cancelled_by_host" */
  onCancelIntent?: () => void;
}

export const ReservationRequestDetails =
  defineComponent<ReservationRequestDetailsProps>({
    name: "ReservationRequestDetails",
    props: [
      "request",
      "linkedReservation",
      "isOpen",
      "onClose",
      "onAcceptIntent",
      "onRejectIntent",
      "onCancelIntent",
    ],
    setup(props) {
      const { user } = useAuth();
      const { t } = useLocalization();
      const timezone = user.value?.restaurant?.timezone || "UTC";

      // Comments (from linked reservation)
      const linkedComments = computed(() => {
        return (props.linkedReservation as any)?.comments || [];
      });

      const handleCancelRequest = () => {
        if (!confirm(t("reservations.request.confirm_cancel"))) return;
        // Emit intent to parent — parent handles the mutation
        props.onCancelIntent?.();
      };

      return () => {
        if (!props.request) return null;

        const r = props.request;
        const isPending = r.status === "pending";
        const statusConfig = STATUS_CONFIG[
          r.status as ReservationRequestStatus
        ] || {
          labelKey: r.status,
          bgClass: "bg-gray-500/10",
          textClass: "",
          icon: "help",
        };

        const phone = r.party_phone;
        const email = r.party_email || r.customer_email;
        const hasTables =
          props.linkedReservation?.table_assignments &&
          props.linkedReservation.table_assignments.length > 0;

        let statusBg = `${statusConfig.bgClass} ${statusConfig.textClass}`;
        if (isPending) statusBg = "bg-error/15 text-error";

        const requestedTags = r.requested_tags || [];

        return (
          <RightSheet
            title={
              <div class="flex items-center gap-2.5">
                <span class="text-accent font-bold text-base">
                  {new Date(r.requested_time).toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
                <span class="text-accent font-bold text-base">
                  {formatTime(r.requested_time, timezone)}
                </span>
                <span
                  class={`px-2.5 py-1 rounded-md text-xs font-semibold leading-5 ${statusBg}`}
                >
                  {isPending
                    ? t("reservations.status.pending")
                    : t(statusConfig.labelKey)}
                </span>
              </div>
            }
            isOpen={props.isOpen}
            onClose={props.onClose}
            width="480px"
          >
            <div class="flex flex-col h-full">
              <div class="flex-1 overflow-y-auto">
                <div class="p-4 space-y-4">
                  {/* ── Line 1: Name | Guests + Duration | Table ── */}
                  <div class="flex items-center gap-3">
                    <span class="text-accent font-semibold text-base truncate">
                      {r.party_name ||
                        r.customer_name ||
                        t("reservations.general.guest_label")}
                    </span>
                    <div class="flex items-center gap-3 flex-shrink-0">
                      <span class="flex items-center gap-1 text-sm font-semibold text-accent">
                        <span class="material-symbols-outlined text-base text-base-content/60">
                          group
                        </span>
                        {r.party_size}
                      </span>
                      {r.duration_mins && (
                        <span class="flex items-center gap-1 text-sm font-semibold text-accent">
                          <span class="material-symbols-outlined text-base text-base-content/60">
                            schedule
                          </span>
                          {r.duration_mins}m
                        </span>
                      )}
                    </div>
                    {/* Table on right — if linked reservation has assignments */}
                    <div class="ml-auto flex-shrink-0">
                      {hasTables ? (
                        <div class="flex gap-1.5">
                          {props.linkedReservation!.table_assignments!.map(
                            (a: TableAssignment) => (
                              <span
                                key={a.table_id}
                                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-purple-500/10 text-purple-400 border border-purple-500/20"
                              >
                                <span class="material-symbols-outlined text-sm">
                                  table_restaurant
                                </span>
                                {a.table_name}
                              </span>
                            ),
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* ── Line 2: Email (left) + Phone (right) ── */}
                  <div class="flex items-center justify-between text-xs">
                    {email ? (
                      <ExternalLink
                        href={`mailto:${email}`}
                        class="flex items-center gap-1.5 text-primary/80 hover:text-primary transition-colors"
                      >
                        <span class="material-symbols-outlined text-sm">
                          mail
                        </span>
                        <span class="truncate">{email}</span>
                      </ExternalLink>
                    ) : (
                      <span class="text-base-content/30">
                        {t("reservations.general.no_email")}
                      </span>
                    )}
                    {phone ? (
                      <ExternalLink
                        href={`tel:${phone}`}
                        class="flex items-center gap-1.5 text-primary/80 hover:text-primary transition-colors"
                      >
                        <span class="material-symbols-outlined text-sm">
                          call
                        </span>
                        <span>{phone}</span>
                      </ExternalLink>
                    ) : (
                      <span class="flex items-center gap-1.5 text-base-content/30">
                        <span class="material-symbols-outlined text-sm">
                          call
                        </span>
                        {t("reservations.general.no_phone")}
                      </span>
                    )}
                  </div>

                  {/* ── Divider ── */}
                  <div class="border-t border-base-content/5" />

                  {/* ── Experiences (requested tags) ── */}
                  {requestedTags.length > 0 && (
                    <div>
                      <h4 class="text-xs font-semibold text-base-content/45 uppercase tracking-wider mb-2">
                        {t("reservations.details.requested_experiences")}
                      </h4>
                      <div class="flex flex-wrap gap-1.5">
                        {requestedTags.map((tag) => (
                          <span
                            key={tag.id}
                            class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-current/15"
                            style={{
                              backgroundColor: tag.color
                                ? `${tag.color}20`
                                : "oklch(var(--b3))",
                              color: tag.color || undefined,
                            }}
                          >
                            {tag.icon && (
                              <span class="material-symbols-outlined text-sm">
                                {tag.icon}
                              </span>
                            )}
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Guest Notes / Special Requests ── */}
                  {r.notes && (
                    <div class="flex items-start gap-2 px-3 py-2 rounded-md bg-teal-500/8 border border-teal-500/15">
                      <span class="material-symbols-outlined text-sm text-teal-400 mt-0.5">
                        sticky_note_2
                      </span>
                      <p class="text-xs text-base-content/70 leading-relaxed">
                        {r.notes}
                      </p>
                    </div>
                  )}

                  {/* ── Internal Comments (from linked reservation) ── */}
                  {linkedComments.value.length > 0 && (
                    <div>
                      <h4 class="text-xs font-semibold text-base-content/45 uppercase tracking-wider mb-2">
                        {t("reservations.details.internal_comments")}
                      </h4>
                      <div class="space-y-1.5">
                        {linkedComments.value.map((comment: any) => (
                          <div
                            key={comment.id}
                            class="flex items-start gap-2 px-3 py-2 rounded-md bg-base-300 border border-amber-500/10"
                          >
                            <div class="flex-1 min-w-0">
                              <div class="flex items-center justify-between gap-2">
                                <span class="text-[11px] font-semibold text-amber-400">
                                  {comment.user_name ||
                                    t("reservations.details.unknown_user")}
                                </span>
                                <span class="text-[10px] text-base-content/40">
                                  {new Date(comment.created_at).toLocaleString(
                                    undefined,
                                    {
                                      month: "short",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    },
                                  )}
                                </span>
                              </div>
                              <p class="text-xs text-accent mt-0.5">
                                {comment.message}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Timeline ── */}
                  <div>
                    <h4 class="text-xs font-semibold text-base-content/45 uppercase tracking-wider mb-2">
                      {t("reservations.request.timeline_header")}
                    </h4>
                    <div class="space-y-1.5 text-xs">
                      <div class="flex items-center gap-2">
                        <div class="w-1.5 h-1.5 rounded-full bg-base-content/20" />
                        <span class="text-base-content/50">
                          {t("reservations.request.submitted")}
                        </span>
                        <span class="ml-auto text-base-content/50">
                          {new Date(r.created_at).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                      {r.updated_at !== r.created_at && (
                        <div class="flex items-center gap-2">
                          <div class="w-1.5 h-1.5 rounded-full bg-primary" />
                          <span class="text-base-content/50">
                            {t("reservations.request.updated")}
                          </span>
                          <span class="ml-auto text-base-content/50">
                            {new Date(r.updated_at).toLocaleString(undefined, {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Footer Actions ── */}
              <div class="flex-shrink-0 p-3 border-t border-base-content/10 bg-base-100 space-y-2">
                {isPending && props.onAcceptIntent && props.onRejectIntent && (
                  <div class="flex gap-2">
                    <button
                      type="button"
                      class="btn btn-sm btn-success flex-1"
                      onClick={() => props.onAcceptIntent!()}
                    >
                      <span class="material-symbols-outlined text-sm">
                        check
                      </span>
                      {t("reservations.general.confirm")}
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-ghost text-base-content/40 hover:text-error hover:bg-error/10 flex-1"
                      onClick={() => props.onRejectIntent!()}
                    >
                      <span class="material-symbols-outlined text-sm">
                        close
                      </span>
                      {t("reservations.general.decline")}
                    </button>
                  </div>
                )}

                {r.status === "confirmed" && props.onCancelIntent && (
                  <button
                    type="button"
                    class="btn btn-sm btn-ghost text-error/60 hover:text-error hover:bg-error/10 w-full"
                    onClick={handleCancelRequest}
                  >
                    <span class="material-symbols-outlined text-sm">
                      cancel
                    </span>
                    {t("reservations.request.cancel_request")}
                  </button>
                )}
              </div>
            </div>
          </RightSheet>
        );
      };
    },
  });
